import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { FormControl } from '@angular/forms';
import { AnimalModel } from '../models/animal.model';
import {
  catchError,
  combineLatest,
  finalize,
  forkJoin,
  of,
  take,
  takeWhile,
  throwError
} from 'rxjs';
import { AnimalOriginModel } from '../models/animal-origin.model';
import { CompetentAuthoritiesRepository } from 'src/app/shared/states/competent-authorities.repository';
import { TranslocoService } from '@jsverse/transloco';
import { AnimalOriginRepository, originStore } from '../states/animal-origin.repository';
import { AnimalOriginReasonForTransfer } from "../models/animal-origin-reason-for-transfer.enum";
import { CompetentAuthorityModel } from 'src/app/shared/models/competent-authority.model';
import { ContactRepository } from "../../contact/states/contact.repository";
import { ContactModel, PhoneNumbersEntryType } from "../../contact/models/contact.model";
import { HelperService } from "../../../shared/services/helper.service";
import { PersonTitleModel } from 'src/app/shared/models/person-title.model';
import { AnimalRepository } from '../states/animal.repository';
import { ToastService } from 'src/app/shared/services/toast.service';
import { AnimalSheltersRepository } from "../../../shared/states/animal-shelter.repository";
import { AnimalShelterModel } from "../../../shared/models/animal-shelter.model";
import { select } from '@ngneat/elf';
import { CARE_OPTIONS, CareCondition, NUTRITION_OPTIONS, NutritionCondition, TEMPERAMENT_OPTIONS, TemperamentCondition } from '../models/body-condition-score.model';
import { RestService } from 'src/app/shared/services/rest.service';
import { AnimalContactTypeEnum } from '../models/contact-type.enum';
import { SentEmailModel } from '../models/sent-email.model';

@Component({
  selector: 'app-animal-details-origin',
  templateUrl: './animal-details-origin.component.html',
  styleUrls: ['./animal-details-origin.component.scss'],
  standalone: false
})
export class AnimalDetailsOriginComponent implements OnInit, OnDestroy, OnChanges {
  onSendFoundReportSent(authorityInformation: string | null): void {
    this.sendFoundReportDialogVisible = false;
    const originId = this.sendFoundReportOriginId;
    if (originId && this.animalOrigItems[originId]) {
      const updated = {
        ...this.animalOrigItems[originId],
        reportSentOn: new Date(),
        authorityInformation: authorityInformation ?? null
      };
      this.animalOrigItems[originId] = updated;
      this.loadSentEmails([originId]);
      this._animalOriginRepository.update(originId, updated, this.animal.id).pipe(take(1)).subscribe();
      this.cdr.detectChanges();
    }
  }

  private loadSentEmails(originIds: number[]): void {
    originIds.forEach(id => {
      this._restService.httpGet(`/sent_emails?animalOriginId=${id}`)
        .pipe(take(1))
        .subscribe((emails: SentEmailModel[]) => {
          this.sentEmailsByOriginId[id] = emails ?? [];
          this.cdr.detectChanges();
        });
    });
  }
  @Input() animal!: AnimalModel;
  @Input() singleItemId: number | null = null;
  alive: boolean = true;
  animalOrigItems: { [id: number]: AnimalOriginModel | any } = {} as { [id: number]: AnimalOriginModel | any };
  cityName: { [id: string]: string | undefined } = {};
  isEditingMode: boolean = false;
  originReasonForTransfer: { name: string; value: string; }[] = [];
  selectedOptionReasonTransfer: { name: string; value: string } | null = null;
  originSubmittedPersonSalutation: PersonTitleModel[] = [];
  contacts: { [id: string]: ContactModel } = {};
  originCompetentAuthorityOptions: CompetentAuthorityModel[] = [];
  editType: any;
  editDialogOriginKey: number = 0;
  openEdit: boolean = false;
  editingItem: any;
  animalOriginReasonForTransfer = AnimalOriginReasonForTransfer;

  contact: ContactModel = {} as ContactModel;
  existingContact: boolean = false;
  existingContactUpdated: boolean = false;
  validationStatusChanged: boolean = true;

  animalSheltersById: { [id: number]: AnimalShelterModel } = {};
  careOptions = CARE_OPTIONS;
  nutritionOptions = NUTRITION_OPTIONS;
  temperamentOptions = TEMPERAMENT_OPTIONS;
  phoneNumbersEntryType = PhoneNumbersEntryType;

  pdfDialogVisible = false;
  pdfBlob?: Blob;
  sendFoundReportDialogVisible: boolean = false;
  sendFoundReportOriginId: number | null = null;
  sendFoundReportContacts: ContactModel[] = [];
  sendFoundReportIsSecuring: boolean = false;

  editingContactRef: any = null;     // AnimalContactRef
  editingContactRefIndex: number = -1;
  editingContact: ContactModel = {} as ContactModel;

  sentEmailsByOriginId: { [originId: number]: SentEmailModel[] } = {};

  sentEmailsDialogVisible = false;
  sentEmailsDialogData: SentEmailModel[] = [];

  openSentEmailsDialog(event: Event, emails: SentEmailModel[]): void {
    event.preventDefault();
    event.stopPropagation();
    this.sentEmailsDialogData = emails ?? [];
    this.sentEmailsDialogVisible = true;
  }

  bcsCareCtrl = new FormControl<CareCondition[] | null>(null);
  bcsNutritionCtrl = new FormControl<NutritionCondition | null>(null);
  bcsTemperamentCtrl = new FormControl<TemperamentCondition[] | null>(null);

  // Optionen für types im Select
  contactTypeOptions: { name: string; value: any }[] = []; // AnimalContactTypeEnum values

  constructor(
    private readonly competentAuthoritiesRepository: CompetentAuthoritiesRepository,
    private readonly _translocoService: TranslocoService,
    private readonly _animalOriginRepository: AnimalOriginRepository,
    private readonly _animalRepository: AnimalRepository,
    private readonly cdr: ChangeDetectorRef,
    private readonly _contactRepository: ContactRepository,
    private readonly _helperService: HelperService,
    private readonly _toast: ToastService,
    private readonly _animalSheltersRepository: AnimalSheltersRepository,
    private readonly _restService: RestService
  ) {
    //Silence is golden
  }

  async ngOnInit(): Promise<void> {
    await this._helperService.ensureActiveLangLoaded();

    this.contactTypeOptions = this._helperService.getEnumOptions(
      AnimalContactTypeEnum,
      'animalContact.types',
      true
    );

    this.originSubmittedPersonSalutation = this._helperService.getSalutationOptions();
    this.setReasonsForTransferOptions();

    if (this.singleItemId !== null) {
      this.initSingleItemMode();
    } else {
      this.getOrigins();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.singleItemId !== null) return;
    if (changes['animal'] && this.animal?.id) {
      this.getOrigins();
    }
  }

  private initSingleItemMode(): void {
    this.competentAuthoritiesRepository
      .getCompetentAuthoritiesByShelterId(this.animal?.animalShelter?.id)
      .pipe(take(1), takeWhile(() => this.alive))
      .subscribe(competentAuthorities => {
        if (competentAuthorities.length) {
          this.originCompetentAuthorityOptions = competentAuthorities;
          competentAuthorities.forEach(ca => { this.cityName[ca.id] = ca.name; });
        }
      });

    originStore.pipe(
      select(state => state.origins[this.animal?.id] ?? []),
      takeWhile(() => this.alive)
    ).subscribe(origins => {
      const filtered = origins.filter(o => o.id === this.singleItemId);
      if (filtered.length) {
        this.setAnimalOrigins(filtered);
      }
    });
  }

  getOrigins(cached: boolean = true) {
    combineLatest([
      this._animalOriginRepository.getByAnimalId(this.animal?.id, cached),
      this.competentAuthoritiesRepository.getCompetentAuthoritiesByShelterId(this.animal?.animalShelter?.id),
    ]).pipe(takeWhile(() => this.alive)).subscribe(([animalOrigins, competentAuthorities]) => {
      if (competentAuthorities.length) {
        this.originCompetentAuthorityOptions = competentAuthorities;
        competentAuthorities.forEach((competentAuthority: CompetentAuthorityModel) => {
          this.cityName[competentAuthority.id] = competentAuthority.name;
        });
      }
      if (animalOrigins?.length) {
        this.setAnimalOrigins(animalOrigins);
      }
    });

    originStore.pipe(
      select(state => state.origins[this.animal?.id] ?? []),
      takeWhile(() => this.alive)
    ).subscribe(origins => {
      this.setAnimalOrigins(origins);
    });
  }

  setAnimalOrigins(animalOrigins: AnimalOriginModel[]) {
    this.animalOrigItems = {};
    this.setReasonsForTransferOptions();

    const contactIds = Array.from(new Set(
      animalOrigins
        .flatMap(o => (o.contacts ?? []).map(r => r.contact))
        .filter((id): id is number => typeof id === 'number')
    ));

    const contactObservables = contactIds.length
      ? forkJoin(contactIds.map(id => this._contactRepository.getById(id).pipe(take(1))))
      : of([]);

    const shelterIds = Array.from(new Set(
      animalOrigins
        .filter(o => o.animalShelter?.id != null)
        .map(o => o.animalShelter!.id)
    ));
    const shelterObservables = shelterIds.length
      ? forkJoin(shelterIds.map(id => this._animalSheltersRepository.get(id)))
      : of([]);

    forkJoin([contactObservables, shelterObservables]).subscribe(([loadedContacts, shelters]: any) => {
      (loadedContacts ?? []).forEach((c: ContactModel) => {
        if (!c?.id) return;
        this.contacts[c.id] = c;
        const b = this.contacts[c.id].birthday;
        if (typeof b === 'string') {
          this.contacts[c.id].birthday = new Date(b);
        }
      });

      (shelters ?? []).forEach((s: AnimalShelterModel) => {
        if (s?.id) this.animalSheltersById[s.id] = s;
      });

      animalOrigins.forEach(o => this.processAnimalOrigin(o));

      const reportOriginIds = animalOrigins
        .filter(o => this.hasReportPdf(o) && o.id)
        .map(o => o.id);
      if (reportOriginIds.length) {
        this.loadSentEmails(reportOriginIds);
      }

      this.cdr.detectChanges();
    });
  }

  private processAnimalOrigin(animalOrigin: AnimalOriginModel) {
    if (!animalOrigin.id) {
      return;
    }
    const id = animalOrigin.id;

    animalOrigin.dateOfReceipt = animalOrigin.dateOfReceipt
      ? new Date(animalOrigin.dateOfReceipt)
      : null;

    this.animalOrigItems[id] = { ...animalOrigin };

    this.selectedOptionReasonTransfer = this.originReasonForTransfer.find(
      option => option.value === this.animalOrigItems[id]?.type
    ) || null;
  }

  setReasonsForTransferOptions() {
    this.originReasonForTransfer = [];
    this.originReasonForTransfer = this._helperService.getEnumOptions(AnimalOriginReasonForTransfer, 'animalAdd.origin.reasonForTransferSelect', true);
  }

  getContactTypeLabel(type: any): string {
    return this._translocoService.translate('animalContact.types.' + type);
  }

  openAddContactRef(originId: number) {
    if (this.hasReportPdf(this.animalOrigItems[originId])) return;
    this.editDialogOriginKey = originId;
    this.editType = 'contactRef';
    this.openEdit = true;
    this.isEditingMode = true;

    this.editingContactRefIndex = -1;
    this.editingContactRef = { type: null, label: '', contact: null, isPrimary: false }; // primary ignoriert
    this.editingContact = {} as ContactModel;

    this.existingContact = false;
    this.existingContactUpdated = false;
    this.validationStatusChanged = true;
  }

  openEditContactRef(originId: number, ref: any, index: number) {
    if (this.hasReportPdf(this.animalOrigItems[originId])) return;
    this.editDialogOriginKey = originId;
    this.editType = 'contactRef';
    this.openEdit = true;
    this.isEditingMode = true;

    this.editingContactRefIndex = index;
    this.editingContactRef = JSON.parse(JSON.stringify(ref));

    const contactId = ref?.contact;
    if (contactId && this.contacts[contactId]) {
      this.editingContact = JSON.parse(JSON.stringify(this.contacts[contactId]));
      this.contact = this.editingContact;
      this.existingContact = true;
    } else {
      this.editingContact = {} as ContactModel;
      this.contact = {} as ContactModel;
      this.existingContact = false;
    }

    this.existingContactUpdated = false;
    this.validationStatusChanged = true;
  }

  removeContactRef(originId: number, ref: any, index: number) {
    const origin = this.animalOrigItems[originId];
    if (!origin || this.hasReportPdf(origin)) return;

    const next = {
      ...origin,
      contacts: (origin.contacts ?? []).filter((_: any, i: number) => i !== index)
    };

    this._animalOriginRepository.update(originId, next, this.animal.id)
      .pipe(take(1))
      .subscribe(() => {
        this._animalRepository.updateAnimalStore(this.animal.id, { updatedAt: new Date() });
        this._toast.showSuccess(this._translocoService.translate('toast.saveSuccess'));
      });
  }

  toggleEditDialog(open: boolean, editType: string, originKey: number) {
    if (open) {
      if (editType === 'general') {
        this.editingItem = JSON.parse(JSON.stringify(this.animalOrigItems[originKey]));
        this.editingItem.competentAuthorityId = this.editingItem.competentAuthority?.id ?? null;
        this.editingItem.dateOfReceipt = this.editingItem.dateOfReceipt
          ? new Date(this.editingItem.dateOfReceipt)
          : null;
        this.bcsCareCtrl.setValue(this.editingItem.careCondition ?? null);
        this.bcsNutritionCtrl.setValue(this.editingItem.bcsNutrition ?? null);
        this.bcsTemperamentCtrl.setValue(this.editingItem.temperament ?? null);
        this.validateGeneralForm();
      }
      this.editType = editType;
      this.editDialogOriginKey = originKey;
      this.isEditingMode = true;
      this.openEdit = open

    } else {
      this.selectedOptionReasonTransfer = this.animalOrigItems[originKey]?.type
        ? this.originReasonForTransfer.find(
          option => option.value === this.animalOrigItems[originKey].type
        ) || null
        : null;

      this.openEdit = open;
      this.editDialogOriginKey = 0;
      this.editingItem = null;
      this.isEditingMode = false;
    }
  }

  openPdf($event: Event, animalOrigin: any) {
    $event.preventDefault();
    $event.stopPropagation();

    const url = `/assets/${animalOrigin.reportPdfPath}`;

    this._restService.httpGet(url, { responseType: 'blob' }).subscribe(blob => {
      this.pdfBlob = blob;
      this.pdfDialogVisible = true;
    });
  }

  openSendPdf($event: Event, animalOrigin: any): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.sendFoundReportOriginId = animalOrigin.id;
    this.sendFoundReportIsSecuring = animalOrigin.type === AnimalOriginReasonForTransfer.SECURED_BY_POLICE;
    const authorityId = animalOrigin.competentAuthority?.id;
    const authority = this.originCompetentAuthorityOptions.find(a => a.id === authorityId);
    this.sendFoundReportContacts = authority?.contacts ?? [];
    this.sendFoundReportDialogVisible = true;
  }

  hasReportPdf(origItem: any): boolean {
    return (
      (origItem?.type === AnimalOriginReasonForTransfer.FOUND_ANIMAL ||
        origItem?.type === AnimalOriginReasonForTransfer.SECURED_BY_POLICE) &&
      !!origItem?.reportPdfPath?.length
    );
  }

  onContactUpdate($event: any) {
    this.contact = $event;
  }

  onValidationStatusChanged($event: boolean) {
    this.validationStatusChanged = $event;
  }

  onExistingContactUpdated($event: boolean) {
    this.existingContactUpdated = $event;
  }

  onExistingContactSelected($event: ContactModel) {
    this.contact = $event;
    this.existingContact = Object.keys($event).length ? true : false;
  }

  validateGeneralForm(): void {
    this.editingItem.type = this.selectedOptionReasonTransfer?.value ?? null;

    const needsCompetentAuthority =
      this.editingItem.type === AnimalOriginReasonForTransfer.FOUND_ANIMAL ||
      this.editingItem.type === AnimalOriginReasonForTransfer.WILD_FOUND ||
      this.editingItem.type === AnimalOriginReasonForTransfer.TAKE_AWAY;

    if (!needsCompetentAuthority) {
      this.editingItem.competentAuthorityId = null;
    }

    const hasRequiredFields =
      !!this.editingItem.dateOfReceipt &&
      !!this.editingItem.type &&
      (!needsCompetentAuthority || this.editingItem.competentAuthorityId != null);

    this.validationStatusChanged = hasRequiredFields;
  }

  getCareLabel(value: CareCondition): string {
    const key = this.careOptions.find(o => o.value === value)?.description;
    return key ? this._translocoService.translate(key) : value;
  }
  getNutritionLabel(value: NutritionCondition): string {
    const key = this.nutritionOptions.find(o => o.value === value)?.description;
    return key ? this._translocoService.translate(key) : value;
  }
  getTemperamentLabel(value: TemperamentCondition): string {
    const key = this.temperamentOptions.find(o => o.value === value)?.description;
    return key ? this._translocoService.translate(key) : value;
  }

  formatTime(value: string | Date): string {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    const hh = d.getHours().toString().padStart(2, '0');
    const mm = d.getMinutes().toString().padStart(2, '0');
    return `${hh}:${mm}`;
  }

  hasAnyBcs(o: any): boolean {
    return !!(o?.careCondition?.length || o?.bcsNutrition || o?.temperament?.length);
  }

  hasAnyFoundSpecific(o: any): boolean {
    return !!(
      o?.finderTransferMode ||
      o?.pickedUpTime ||
      o?.pickedUpByName ||
      o?.foundStatus ||
      o?.pickupDateTime ||
      o?.pickupUserName ||
      o?.finderDataMayBeShared === true ||
      o?.animalRemainsWithFinder === true
    );
  }

  save(animalOriginItem: any) {
    if (this.editType === 'contactRef') {
      this.saveContactRef();
      return;
    }
    if (this.editType === 'general') {
      animalOriginItem = {
        ...animalOriginItem,
        competentAuthority: this.editingItem.competentAuthorityId
          ? { id: this.editingItem.competentAuthorityId }
          : null
      };
      this.updateAnimalOrigin(animalOriginItem);
    }
    this.toggleEditDialog(false, '', 0);
  }

  private saveContactRef() {
    const originId = this.editDialogOriginKey;
    const origin = this.animalOrigItems[originId] as AnimalOriginModel;
    if (!origin) return;

    const finalizeOriginUpdate = (contactId: number | null) => {
      const refs = [...(origin.contacts ?? [])];

      const nextRef = {
        ...this.editingContactRef,
        contact: contactId,
        // id bleibt erhalten wenn vorhanden (join-record id!)
        // isPrimary wird ignoriert, aber wird mitgeschickt
      };

      if (this.editingContactRefIndex >= 0) {
        refs[this.editingContactRefIndex] = nextRef;
      } else {
        refs.push(nextRef);
      }

      const payload: any = { ...origin, contacts: refs };

      this._animalOriginRepository.update(originId, payload, this.animal.id)
        .pipe(take(1))
        .subscribe(() => {
          this.toggleEditDialog(false, '', 0);
          this._animalRepository.updateAnimalStore(this.animal.id, { updatedAt: new Date() });
          this._toast.showSuccess(this._translocoService.translate('toast.saveSuccess'));
        });
    };
    // Existing contact with ID => always update (idempotent, also catches edited fields)
    if (this.contact?.id) {
      const contactId = this.contact.id;
      const { id: _id, ...contactPayload } = this.contact as any;
      this._contactRepository.update(contactPayload, contactId)
        .pipe(take(1))
        .subscribe((updated) => {
          this.contacts[updated.id] = updated;
          finalizeOriginUpdate(updated.id);
        });
      return;
    }

    // No contact ID in this.contact (was reset or never set) — preserve original ref
    const fallbackId = typeof this.editingContactRef?.contact === 'number' ? this.editingContactRef.contact : null;
    const contactIsEmpty = !this.contact || Object.keys(this.contact).length === 0;
    if (contactIsEmpty) {
      finalizeOriginUpdate(fallbackId);
      return;
    }

    // New contact (has data but no ID) => create
    this._contactRepository.create(this.contact)
      .pipe(take(1))
      .subscribe((created) => {
        this.contacts[created.id] = created;
        finalizeOriginUpdate(created.id);
      });
  }

  updateAnimalOrigin(animalOriginItem: AnimalOriginModel | any): void {
    let dateOfReceipt = null;
    if (animalOriginItem.dateOfReceipt) {
      dateOfReceipt = this._helperService.determineDateOfReceipt(
        this.animalOrigItems[this.editDialogOriginKey].dateOfReceipt,
        animalOriginItem.dateOfReceipt
      );
    }
    const originPayload: AnimalOriginModel | any = {
      ...animalOriginItem,
      dateOfReceipt,
      animal: { id: this.animal.id },
      type: animalOriginItem.type,
      specialInformation: animalOriginItem.specialInformation,
      location: animalOriginItem.location,
      otherComments: animalOriginItem.otherComments,
      conditionOfCare: animalOriginItem.conditionOfCare,
      nutritionalStatus: animalOriginItem.nutritionalStatus,
      careCondition: this.bcsCareCtrl.value ?? null,
      bcsNutrition: this.bcsNutritionCtrl.value ?? null,
      temperament: this.bcsTemperamentCtrl.value ?? null,
    };

    if (animalOriginItem.competentAuthority) {
      originPayload.competentAuthority = { id: animalOriginItem.competentAuthority.id };
    }

    if (animalOriginItem.type !== 'found_animal'
      && animalOriginItem.type !== 'take_away'
      && animalOriginItem.type !== 'wild_found') {
      originPayload.competentAuthority = null;
    }

    this._animalOriginRepository.update(this.editDialogOriginKey, originPayload, this.animal.id)
      .pipe(
        take(1),
        catchError((error) => {
          this._toast.showError(this._translocoService.translate('toast.saveError'));
          console.error('Fehler beim Aktualisieren des Herkunft:', error);
          return throwError(() => error);
        }),
        finalize(() => {
          this.cdr.detectChanges();
        })
      )
      .subscribe(() => {
        if (this.animalOrigItems[this.editDialogOriginKey]) {
          this.animalOrigItems[this.editDialogOriginKey] = {
            ...this.animalOrigItems[this.editDialogOriginKey],
            ...originPayload
          };
        }
        this._animalRepository.updateAnimalStore(this.animal.id, { updatedAt: new Date() });
        this._toast.showSuccess(this._translocoService.translate('toast.saveSuccess'));
      });

  }


  ngOnDestroy(): void {
    this.alive = false;
  }
}
